import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ExternalLink, ArrowRight, Boxes, Code2, Wrench } from "lucide-react";
import { useApp } from "@/context/AppContext";

const OLIVE = "#3D3B00";
const GREEN = "#2F9E5A";

const HEAD = {
  nl: { kicker: "Ecosysteem", title: "Onze partners", sub: "Samen leveren we de complete kiosk-oplossing voor de recreatiebranche: software, hardware, installatie en service. Dit zijn de partners waarmee we samenwerken." },
  en: { kicker: "Ecosystem", title: "Our partners", sub: "Together we deliver the complete kiosk solution for the leisure sector: software, hardware, installation and service. These are the partners we work with." },
  de: { kicker: "Ökosystem", title: "Unsere Partner", sub: "Gemeinsam liefern wir die komplette Kiosklösung für die Freizeitbranche: Software, Hardware, Installation und Service. Das sind die Partner, mit denen wir zusammenarbeiten." },
  fr: { kicker: "Écosystème", title: "Nos partenaires", sub: "Ensemble, nous proposons la solution kiosque complète pour le secteur des loisirs : logiciel, matériel, installation et service. Voici les partenaires avec qui nous travaillons." },
  es: { kicker: "Ecosistema", title: "Nuestros partners", sub: "Juntos ofrecemos la solución de kiosco completa para el sector del ocio: software, hardware, instalación y servicio. Estos son los partners con los que trabajamos." },
  it: { kicker: "Ecosistema", title: "I nostri partner", sub: "Insieme offriamo la soluzione kiosk completa per il settore del tempo libero: software, hardware, installazione e assistenza. Questi sono i partner con cui collaboriamo." },
  da: { kicker: "Økosystem", title: "Vores partnere", sub: "Sammen leverer vi den komplette kiosk-løsning til fritidsbranchen: software, hardware, installation og service. Det er de partnere, vi samarbejder med." },
};

const GROUPS = [
  {
    id: "software",
    icon: Code2,
    title: { nl: "Software & boekingsplatformen", en: "Software & booking platforms", de: "Software & Buchungsplattformen", fr: "Logiciels & plateformes de réservation", es: "Software y plataformas de reservas", it: "Software e piattaforme di prenotazione", da: "Software & bookingplatforme" },
    items: [
      { name: "Booking Experts", initials: "BE", color: "#1F6FEB", logo: "/brand/partners/bookingexperts.png",
        desc: { nl: "PMS en boekingsplatform voor verblijfsrecreatie.", en: "PMS and booking platform for accommodation businesses.", de: "PMS und Buchungsplattform für Beherbergungsbetriebe.", fr: "PMS et plateforme de réservation pour l'hébergement de plein air.", es: "PMS y plataforma de reservas para alojamientos turísticos.", it: "PMS e piattaforma di prenotazione per strutture ricettive.", da: "PMS og bookingplatform til overnatningsvirksomheder." },
        url: "https://www.bookingexperts.com/nl/" },
      { name: "Recranet", initials: "RC", color: "#0E9F6E", logo: "/brand/partners/recranet.svg",
        desc: { nl: "Boekings- en beheersoftware voor de recreatiebranche.", en: "Booking and management software for the leisure sector.", de: "Buchungs- und Verwaltungssoftware für die Freizeitbranche.", fr: "Logiciel de réservation et de gestion pour le secteur des loisirs.", es: "Software de reservas y gestión para el sector del ocio.", it: "Software di prenotazione e gestione per il settore del tempo libero.", da: "Booking- og administrationssoftware til fritidsbranchen." },
        url: "https://recranet.com/nl" },
      { name: "Tommy Booking Support", initials: "TB", color: "#E8590C", logo: "/brand/partners/tommy.svg", logoDark: true,
        desc: { nl: "Online boekings- en reserveringssysteem voor recreatie.", en: "Online booking and reservation system for leisure.", de: "Online-Buchungs- und Reservierungssystem für die Freizeitbranche.", fr: "Système de réservation en ligne pour les loisirs.", es: "Sistema de reservas online para el ocio.", it: "Sistema di prenotazione online per il tempo libero.", da: "Online booking- og reservationssystem til fritid." },
        url: "https://tommybookingsupport.com" },
      { name: "Campingcomfort", initials: "CC", color: "#0CA678", logo: "/brand/partners/campingcomfort.png",
        desc: { nl: "Gast-app en online bestel-software voor campings en parken.", en: "Guest app and online ordering software for campsites and parks.", de: "Gäste-App und Online-Bestellsoftware für Campingplätze und Parks.", fr: "Application client et logiciel de commande en ligne pour campings et parcs.", es: "App para huéspedes y software de pedidos online para campings y parques.", it: "App per gli ospiti e software di ordini online per campeggi e parchi.", da: "Gæste-app og online bestillingssoftware til campingpladser og parker." },
        url: "https://www.campingcomfort.com/nl" },
    ],
  },
  {
    id: "tech",
    icon: Boxes,
    title: { nl: "Technologie & hardware", en: "Technology & hardware", de: "Technologie & Hardware", fr: "Technologie & matériel", es: "Tecnología y hardware", it: "Tecnologia e hardware", da: "Teknologi & hardware" },
    items: [
      { name: "QX Innovations", initials: "QX", color: "#6D28D9", logo: "/brand/partners/qx-light.svg", logoDark: true,
        desc: { nl: "Software, integraties en fleet-management voor zelfbediening.", en: "Software, integrations and fleet management for self-service.", de: "Software, Integrationen und Flottenmanagement für Selbstbedienung.", fr: "Logiciels, intégrations et gestion de parc pour le libre-service.", es: "Software, integraciones y gestión de flota para el autoservicio.", it: "Software, integrazioni e fleet management per il self-service.", da: "Software, integrationer og fleet-management til selvbetjening." },
        url: "https://qx-innovations.nl/nl/" },
      { name: "QIOX", initials: "QI", color: OLIVE, logo: "/brand/partners/qiox.png", logoDark: true,
        desc: { nl: "Multi-vendor kiosk-hardware voor binnen en buiten.", en: "Multi-vendor kiosk hardware for indoor and outdoor.", de: "Multi-Vendor-Kiosk-Hardware für innen und außen.", fr: "Matériel de kiosque multi-fournisseurs pour l'intérieur et l'extérieur.", es: "Hardware de kiosco multimarca para interior y exterior.", it: "Hardware kiosk multi-vendor per interni ed esterni.", da: "Multi-vendor kiosk-hardware til inde og ude." },
        url: "https://www.qiox.nl/" },
      { name: "Interact Kiosk", initials: "IK", color: "#0B7285", logo: "/brand/partners/interact.svg", logoDark: true,
        desc: { nl: "Robuuste self-service zuilen en betaalterminals.", en: "Robust self-service kiosks and payment terminals.", de: "Robuste Self-Service-Kioske und Bezahlterminals.", fr: "Bornes self-service robustes et terminaux de paiement.", es: "Kioscos de autoservicio robustos y terminales de pago.", it: "Chioschi self-service robusti e terminali di pagamento.", da: "Robuste selvbetjeningsstandere og betalingsterminaler." },
        url: "https://interact-kiosks.com/" },
    ],
  },
  {
    id: "service",
    icon: Wrench,
    title: { nl: "Installatie & service", en: "Installation & service", de: "Installation & Service", fr: "Installation & service", es: "Instalación y servicio", it: "Installazione e assistenza", da: "Installation & service" },
    items: [
      { name: "VYNK Groep", initials: "VY", color: "#B45309", badge: { nl: "Installatiepartner Nederland", en: "Installation partner Netherlands", de: "Installationspartner Niederlande", fr: "Partenaire d'installation Pays-Bas", es: "Partner de instalación Países Bajos", it: "Partner di installazione Paesi Bassi", da: "Installationspartner Holland" },
        desc: { nl: "Onze installatiepartner in Nederland: elektrotechniek, montage en onderhoud op locatie.", en: "Our installation partner in the Netherlands: electrical work, mounting and on-site maintenance.", de: "Unser Installationspartner in den Niederlanden: Elektrotechnik, Montage und Wartung vor Ort.", fr: "Notre partenaire d'installation aux Pays-Bas : électrotechnique, montage et maintenance sur site.", es: "Nuestro partner de instalación en los Países Bajos: electrotecnia, montaje y mantenimiento in situ.", it: "Il nostro partner di installazione nei Paesi Bassi: elettrotecnica, montaggio e manutenzione in loco.", da: "Vores installationspartner i Holland: el-teknik, montering og vedligeholdelse på stedet." },
        url: "https://www.vynkgroep.nl/" },
    ],
  },
];

function LogoTile({ logo, logoDark, initials, color, name }) {
  const [failed, setFailed] = React.useState(false);
  if (logo && !failed) {
    return (
      <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl border overflow-hidden shrink-0 p-1.5"
            style={logoDark ? { background: color, borderColor: color } : { background: "#f6f4ee", borderColor: "#e6e1d2" }}>
        <img src={logo} alt={`${name} logo`} className="max-w-full max-h-full object-contain"
             loading="lazy" onError={() => setFailed(true)} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white font-bold text-sm shrink-0"
          style={{ background: color }} aria-hidden>
      {initials}
    </span>
  );
}

const CTA = {
  nl: { catalog: "Bekijk in catalogus", site: "Bezoek website", interview: "Lees het interview" },
  en: { catalog: "View in catalog", site: "Visit website", interview: "Read the interview" },
  de: { catalog: "Im Katalog ansehen", site: "Website besuchen", interview: "Interview lesen" },
  fr: { catalog: "Voir dans le catalogue", site: "Visiter le site", interview: "Lire l'interview" },
  es: { catalog: "Ver en el catálogo", site: "Visitar el sitio web", interview: "Leer la entrevista" },
  it: { catalog: "Vedi nel catalogo", site: "Visita il sito", interview: "Leggi l'intervista" },
  da: { catalog: "Se i kataloget", site: "Besøg websitet", interview: "Læs interviewet" },
};

const CTA2 = {
  nl: { title: "Partner worden?", sub: "Software, hardware of installatie: laten we het ecosysteem samen uitbreiden.", btn: "Start met configureren" },
  en: { title: "Want to become a partner?", sub: "Software, hardware or installation: let's expand the ecosystem together.", btn: "Start configuring" },
  de: { title: "Partner werden?", sub: "Software, Hardware oder Installation: Lassen Sie uns das Ökosystem gemeinsam erweitern.", btn: "Jetzt konfigurieren" },
  fr: { title: "Devenir partenaire ?", sub: "Logiciel, matériel ou installation : développons l'écosystème ensemble.", btn: "Commencer la configuration" },
  es: { title: "¿Quieres ser partner?", sub: "Software, hardware o instalación: ampliemos juntos el ecosistema.", btn: "Empezar a configurar" },
  it: { title: "Vuoi diventare partner?", sub: "Software, hardware o installazione: ampliamo insieme l'ecosistema.", btn: "Inizia a configurare" },
  da: { title: "Vil du være partner?", sub: "Software, hardware eller installation: lad os udvide økosystemet sammen.", btn: "Start konfiguration" },
};

export default function PartnersPage() {
  const { lang } = useApp();
  const L = (o) => o[lang] || o.nl || o.en;
  const cta = CTA[lang] || CTA.nl;
  const c2 = CTA2[lang] || CTA2.nl;
  const h = HEAD[lang] || HEAD.nl;
  const prefix = lang && lang !== "nl" ? `/${lang}` : "";

  return (
    <div className="bg-[#F4F1EA] min-h-screen" data-testid="partners-page">
      <Helmet><title>{h.title}</title></Helmet>

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
             style={{ background: `${OLIVE}1A`, color: OLIVE }} data-testid="partners-kicker">
          {h.kicker}
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: OLIVE }}>{h.title}</h1>
        <p className="mt-5 text-base sm:text-lg text-[#5b5a3f] max-w-2xl leading-relaxed">{h.sub}</p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24 space-y-14">
        {GROUPS.map((g) => {
          const GI = g.icon;
          return (
            <div key={g.id} data-testid={`partner-group-${g.id}`}>
              <div className="flex items-center gap-2.5 mb-6">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl"
                      style={{ background: `${GREEN}22`, color: GREEN }}>
                  <GI className="w-5 h-5" />
                </span>
                <h2 className="text-lg font-semibold" style={{ color: OLIVE }}>{L(g.title)}</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {g.items.map((p) => (
                  <div key={p.name}
                       className="bg-white rounded-2xl border border-[#e6e1d2] p-6 flex flex-col transition-shadow hover:shadow-lg"
                       data-testid={`partner-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <div className="flex items-center gap-3.5">
                      <LogoTile logo={p.logo} logoDark={p.logoDark} initials={p.initials} color={p.color} name={p.name} />
                      <div>
                        <div className="font-semibold text-[#2c2b1c] leading-tight">{p.name}</div>
                        {p.badge && (
                          <span className="inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: `${GREEN}1A`, color: GREEN }}>
                            {L(p.badge)}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-[#5b5a3f] leading-relaxed flex-1">{L(p.desc)}</p>
                    <div className="mt-5 flex items-center justify-between gap-3">
                      <Link to={`${prefix}/partners/${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                            style={{ color: GREEN }}
                            data-testid={`partner-interview-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                        {cta.interview} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                      {p.url && (
                        <a href={p.url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-xs font-semibold text-[#8a8770] hover:text-[#2c2b1c] transition-colors"
                           title={cta.site}
                           data-testid={`partner-link-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                          {cta.site} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <div className="rounded-2xl p-8 sm:p-10 text-center" style={{ background: OLIVE }} data-testid="partners-cta">
          <h3 className="text-2xl font-bold text-[#F4F1EA]">{c2.title}</h3>
          <p className="mt-2 text-[#d9d4bf] max-w-xl mx-auto text-sm">{c2.sub}</p>
          <Link to={`${prefix}/configurator`}
                className="btn-accent mt-6 inline-flex items-center gap-2"
                data-testid="partners-cta-button">
            {c2.btn} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
